/**
 * Live annotation client state + helpers.
 *
 * The Zustand store holds the currently-rendered annotation set keyed by
 * id. AnnotationOverlay reads it; the sync hook writes it from inbound
 * envelopes; the toolbar's pointer handlers append + flush.
 *
 * Coordinates are NORMALISED [0, 1] in both axes (AGENTS.md §11). The
 * overlay's SVG sets `viewBox=0 0 1 1 preserveAspectRatio=none` so a
 * value of 0.5 sits at the horizontal centre regardless of the actual
 * pixel viewport.
 */
import { create } from "zustand";

import type {
  Annotation,
  AnnotationGeometry,
  AnnotationKind,
} from "./types";

export const ANNOTATION_COLORS = [
  // WCAG 2.2 AA-checked against the cvp-ink (#0B1729) and a dark video
  // backdrop. Yellow/red/green/cyan span the colour-blind axes.
  "#FF3B30", // red
  "#FFD60A", // yellow
  "#34C759", // green
  "#0A84FF", // blue
] as const;

export type ToolKind =
  | "off"
  | "pen"
  | "rect"
  | "ellipse"
  | "text"
  | "polyline"
  | "arrow";

type Store = {
  byId: Record<string, Annotation>;
  /** Active drawing tool on the operator side. `"off"` means the overlay
   * is render-only and pointer-events are passed through to the LiveKit
   * controls underneath (mute / leave / share / settings). */
  tool: ToolKind;
  color: string;
  /** Author's recent local strokes — undo stack. Most recent at end.
   * Tracked separately from byId because undo only targets things THIS
   * client created (mirrors the backend's author check). */
  undoStack: string[];
  /** Replace the live set wholesale (used on initial fetch + clear). */
  replaceAll: (annotations: Annotation[]) => void;
  upsert: (annotation: Annotation) => void;
  remove: (id: string) => void;
  clear: () => void;
  setTool: (tool: ToolKind) => void;
  setColor: (color: string) => void;
  /** Push a locally-authored annotation id for later undo. */
  pushOwnAnnotation: (id: string) => void;
  /** Pop the most-recent locally-authored annotation id, or null when
   * the stack is empty. Caller is responsible for the network DELETE. */
  popOwnAnnotation: () => string | null;
  /** Drop a specific id from the undo stack (called when an undone row
   * comes back via the sync channel as cleared, for instance). */
  forgetOwnAnnotation: (id: string) => void;
};

export const useAnnotationStore = create<Store>((set, get) => ({
  byId: {},
  // Default to "off" so the operator can interact with LiveKit's bottom
  // controls (mute / leave / share) without first deselecting a tool.
  // Click pen / rect / ellipse to arm; click the same tool again or
  // "Off" to deactivate.
  tool: "off",
  color: ANNOTATION_COLORS[0],
  undoStack: [],
  replaceAll: (annotations) => {
    const byId: Record<string, Annotation> = {};
    for (const a of annotations) byId[a.id] = a;
    set({ byId });
  },
  upsert: (a) =>
    set((s) => ({ byId: { ...s.byId, [a.id]: a } })),
  remove: (id) =>
    set((s) => {
      const next = { ...s.byId };
      delete next[id];
      return {
        byId: next,
        undoStack: s.undoStack.filter((x) => x !== id),
      };
    }),
  clear: () => set({ byId: {}, undoStack: [] }),
  setTool: (tool) => set({ tool }),
  setColor: (color) => set({ color }),
  pushOwnAnnotation: (id) =>
    set((s) => ({ undoStack: [...s.undoStack, id] })),
  popOwnAnnotation: () => {
    const stack = get().undoStack;
    if (stack.length === 0) return null;
    const id = stack[stack.length - 1]!;
    set({ undoStack: stack.slice(0, -1) });
    return id;
  },
  forgetOwnAnnotation: (id) =>
    set((s) => ({ undoStack: s.undoStack.filter((x) => x !== id) })),
}));

/* ---------- Geometry helpers ---------- */

/** Convert a series of (x,y) points to an SVG path `d` string. */
export function pointsToPath(points: readonly [number, number][]): string {
  if (points.length === 0) return "";
  const head = points[0]!;
  let d = `M ${head[0]} ${head[1]}`;
  for (let i = 1; i < points.length; i++) {
    const p = points[i]!;
    d += ` L ${p[0]} ${p[1]}`;
  }
  return d;
}

/** Synthesize a draft annotation while the user is mid-drag.
 *
 * `stroke_width` is in normalised viewBox units (the overlay's SVG uses
 * `viewBox=0 0 1 1`). 0.006 ≈ 0.6% of the SVG's pixel width, which is a
 * comfortably-visible stroke at typical viewports. We don't apply
 * `vector-effect=non-scaling-stroke` because that would reinterpret the
 * value as raw pixels and render invisibly thin. */
export function buildDraft(
  kind: AnnotationKind,
  geometry: AnnotationGeometry,
  color: string,
): Pick<Annotation, "kind" | "vector_jsonb" | "color" | "stroke_width"> {
  return {
    kind,
    vector_jsonb: geometry,
    color,
    stroke_width: 0.006,
  };
}

/** Resolve a Tool selection to the kind the API expects. */
export function toolToKind(tool: ToolKind): AnnotationKind {
  if (tool === "rect") return "rect";
  if (tool === "ellipse") return "ellipse";
  if (tool === "text") return "text";
  if (tool === "polyline") return "polyline";
  if (tool === "arrow") return "arrow";
  return "freehand";
}

/** Cheap unique id for client-minted annotations.
 *  We send this to the server so the optimistic SVG matches the persisted
 *  row's id without a roundtrip. */
export function newAnnotationId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}